import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../hooks/useAuth";
import {
  getUserTeams,
  getInvoicesForReport,
  formatCurrency,
  formatDate,
  getCurrentFactorValue,
} from "../services/firebaseService";
import { Team, Invoice, DateRange } from "../types";
import "./PaidReport.css";

const PaidReport: React.FC = () => {
  const { user } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<
    "id" | "date" | "totalValue" | "paidValue"
  >("id");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: "",
    endDate: "",
  });

  const loadUserTeams = async () => {
    try {
      const userTeams = await getUserTeams(user!.uid);
      setTeams(userTeams);
      setSelectedTeams([]);
    } catch (error) {
      console.error("Error loading teams:", error);
    }
  };

  useEffect(() => {
    if (user) {
      loadUserTeams();
      setInitialDateRange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const setInitialDateRange = () => {
    const currentDay = new Date().getDate();
    const currentDate = new Date();
    const lastMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() - 1,
    );
    const nextMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
    );

    let startDate, endDate;

    if (currentDay < 26) {
      startDate = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, "0")}-26`;
      endDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-25`;
    } else {
      startDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-26`;
      endDate = `${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, "0")}-25`;
    }

    setDateRange({ startDate, endDate });
  };

  const loadReportData = async () => {
    if (
      selectedTeams.length === 0 ||
      !dateRange.startDate ||
      !dateRange.endDate
    ) {
      alert("Selecione pelo menos uma equipe e defina o período.");
      return;
    }

    setLoading(true);
    try {
      const allInvoices: Invoice[] = [];

      for (const teamId of selectedTeams) {
        const teamInvoices = await getInvoicesForReport(
          teamId,
          dateRange.startDate,
          dateRange.endDate,
        );
        allInvoices.push(...teamInvoices);
      }

      setInvoices(allInvoices);
    } catch (error) {
      console.error("Error loading report data:", error);
      alert("Erro ao carregar dados do relatório.");
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalFromServices = (services: any[]): number => {
    if (!services || !Array.isArray(services)) return 0;
    return services.reduce((total, service) => {
      if (service) {
        const quantity = parseFloat(service.quantity?.toString() || "0");
        const price = parseFloat(service.price?.toString() || "0");
        const parsed = parseFloat(service.factor?.toString() || "");
        const factor = !isNaN(parsed) && parsed > 0 ? parsed : getCurrentFactorValue();
        return total + quantity * price * factor;
      }
      return total;
    }, 0);
  };

  // In this report, a boletim is considered paid when invoice.paid === true.
  // The paid value for a paid boletim equals its total (price × qty × factor).

  const paidInvoiceData = useMemo(() => {
    return invoices.map((invoice) => {
      const totalValue = calculateTotalFromServices(invoice.services || []);
      const paidValue = invoice.paid ? totalValue : 0;

      return {
        key: invoice.key,
        id: invoice.id,
        title: invoice.title || invoice.header?.title || "",
        si: invoice.pms || invoice.header?.si || "",
        pms: invoice.pms || invoice.header?.pms || "",
        city: invoice.city || invoice.header?.city || "",
        date: formatDate(invoice.date) || invoice.header?.date || "",
        totalValue: totalValue,
        paidValue: paidValue,
        paid: !!invoice.paid,
      };
    });
  }, [invoices]);

  const filteredAndSortedData = useMemo(() => {
    let filtered = paidInvoiceData.filter(
      (item) =>
        searchTerm === "" ||
        item.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.si.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.pms.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.city.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    return filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case "id":
          aValue = a.id;
          bValue = b.id;
          break;
        case "date":
          aValue = a.date;
          bValue = b.date;
          break;
        case "totalValue":
          aValue = a.totalValue;
          bValue = b.totalValue;
          break;
        case "paidValue":
          aValue = a.paidValue;
          bValue = b.paidValue;
          break;
        default:
          aValue = a.id;
          bValue = b.id;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortOrder === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return sortOrder === "asc"
          ? (aValue as number) - (bValue as number)
          : (bValue as number) - (aValue as number);
      }
    });
  }, [paidInvoiceData, searchTerm, sortBy, sortOrder]);

  const reportSummary = useMemo(() => {
    const totalInvoices = filteredAndSortedData.length;
    const totalValue = filteredAndSortedData.reduce(
      (sum, item) => sum + item.totalValue,
      0,
    );
    const totalPaidValue = filteredAndSortedData.reduce(
      (sum, item) => sum + item.paidValue,
      0,
    );
    const pendingValue = totalValue - totalPaidValue;
    const paidInvoices = filteredAndSortedData.filter((item: any) => item.paid).length;

    return {
      totalInvoices,
      paidInvoices,
      pendingInvoices: totalInvoices - paidInvoices,
      totalValue,
      totalPaidValue,
      pendingValue,
    };
  }, [filteredAndSortedData]);

  const handleTeamToggle = (teamId: string) => {
    setSelectedTeams((prev) =>
      prev.includes(teamId)
        ? prev.filter((id) => id !== teamId)
        : [...prev, teamId],
    );
  };

  const handleSelectAllTeams = () => {
    setSelectedTeams(teams.map((team) => team.id));
  };

  const handleDeselectAllTeams = () => {
    setSelectedTeams([]);
  };

  const handleSort = (column: "id" | "date" | "totalValue" | "paidValue") => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const handleViewPDF = (invoiceKey: string) => {
    // Store invoice data for PDF view
    const invoice = invoices.find((inv) => inv.key === invoiceKey);
    if (invoice) {
      sessionStorage.setItem("pdfInvoiceData", JSON.stringify(invoice));
      sessionStorage.setItem("pdfInvoiceKey", invoiceKey);
      // Open PDF in new window/tab
      window.open("/pdf", "_blank");
    }
  };

  const exportToCSV = () => {
    const headers = [
      "ID",
      "Título",
      "PES",
      "PMS",
      "Município",
      "Data",
      "Valor Total",
      "Valor Pago",
      "Valor Pendente",
    ].join(";");

    const csvData = filteredAndSortedData
      .map((item) =>
        [
          item.id,
          `"${item.title}"`,
          `"${item.si}"`,
          `"${item.pms}"`,
          `"${item.city}"`,
          item.date,
          item.totalValue.toFixed(2),
          item.paidValue.toFixed(2),
          (item.totalValue - item.paidValue).toFixed(2),
        ].join(";"),
      )
      .join("\n");

    const csvContent = [headers, csvData].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `relatorio-pagos-${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getSortIcon = (column: string) => {
    if (sortBy !== column) return <i className="fas fa-sort"></i>;
    return sortOrder === "asc" ? (
      <i className="fas fa-sort-up"></i>
    ) : (
      <i className="fas fa-sort-down"></i>
    );
  };

  return (
    <div className="paid-report-container">
      <div className="report-header">
        <div className="header-content">
          <h1>Relatório Pagos</h1>
          <button onClick={() => window.close()} className="close-btn">
            <i className="fas fa-times"></i>
            Fechar
          </button>
        </div>
      </div>

      <div className="report-content">
        {/* Filters Section */}
        <div className="filters-section">
          <div className="filter-group">
            <div className="teams-header">
              <h3>Equipes</h3>
              <div className="team-controls">
                <button onClick={handleSelectAllTeams} className="control-btn">
                  Selecionar Todas
                </button>
                <button
                  onClick={handleDeselectAllTeams}
                  className="control-btn"
                >
                  Desmarcar Todas
                </button>
              </div>
            </div>
            <div className="teams-grid">
              {teams.map((team) => (
                <label key={team.id} className="team-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedTeams.includes(team.id)}
                    onChange={() => handleTeamToggle(team.id)}
                  />
                  {team.name}
                </label>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <h3>Período</h3>
            <div className="date-inputs">
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) =>
                  setDateRange((prev) => ({
                    ...prev,
                    startDate: e.target.value,
                  }))
                }
                className="date-input"
              />
              <span>até</span>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) =>
                  setDateRange((prev) => ({ ...prev, endDate: e.target.value }))
                }
                className="date-input"
              />
            </div>
          </div>

          <div className="filter-group">
            <h3>Filtros</h3>
            <div className="filter-controls">
              <input
                type="text"
                placeholder="Pesquisar ID, título, SI, PMS, município..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          </div>

          <div className="action-buttons">
            <button
              onClick={loadReportData}
              disabled={loading || selectedTeams.length === 0}
              className="load-btn"
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Carregando...
                </>
              ) : (
                <>
                  <i className="fas fa-search"></i>
                  Gerar Relatório
                </>
              )}
            </button>

            {paidInvoiceData.length > 0 && (
              <button onClick={exportToCSV} className="export-btn">
                <i className="fas fa-download"></i>
                Exportar CSV
              </button>
            )}
          </div>
        </div>

        {/* Summary Section */}
        {paidInvoiceData.length > 0 && (
          <div className="summary-section">
            <h3>Resumo</h3>
            <div className="summary-cards">
              <div className="summary-card">
                <div className="card-value">{reportSummary.totalInvoices}</div>
                <div className="card-label">Total de Boletins</div>
              </div>
              <div className="summary-card">
                <div className="card-value">{reportSummary.paidInvoices}</div>
                <div className="card-label">Com Pagamento</div>
              </div>
              <div className="summary-card">
                <div className="card-value">
                  {reportSummary.pendingInvoices}
                </div>
                <div className="card-label">Sem Pagamento</div>
              </div>
              <div className="summary-card">
                <div className="card-value">
                  {formatCurrency(reportSummary.totalValue)}
                </div>
                <div className="card-label">Valor Total</div>
              </div>
              <div className="summary-card">
                <div className="card-value">
                  {formatCurrency(reportSummary.totalPaidValue)}
                </div>
                <div className="card-label">Valor Pago</div>
              </div>
              <div className="summary-card">
                <div className="card-value">
                  {formatCurrency(reportSummary.pendingValue)}
                </div>
                <div className="card-label">Valor Pendente</div>
              </div>
            </div>
          </div>
        )}

        {/* Results Table */}
        {paidInvoiceData.length > 0 && (
          <div className="results-section">
            <div className="results-header">
              <h3>Resultados ({filteredAndSortedData.length} boletins)</h3>
            </div>

            <div className="table-wrapper">
              <table className="report-table">
                <thead>
                  <tr>
                    <th
                      onClick={() => handleSort("id")}
                      className="sortable-header"
                    >
                      ID {getSortIcon("id")}
                    </th>
                    <th>Título</th>
                    <th>PES</th>
                    <th>PMS</th>
                    <th>Município</th>
                    <th
                      onClick={() => handleSort("date")}
                      className="sortable-header"
                    >
                      Data {getSortIcon("date")}
                    </th>
                    <th
                      onClick={() => handleSort("totalValue")}
                      className="sortable-header"
                    >
                      Valor (R$) {getSortIcon("totalValue")}
                    </th>
                    <th
                      onClick={() => handleSort("paidValue")}
                      className="sortable-header"
                    >
                      Valor Pago (R$) {getSortIcon("paidValue")}
                    </th>
                    <th>PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedData.map((item, index) => (
                    <tr
                      key={item.key}
                      className={index % 2 === 0 ? "even-row" : "odd-row"}
                    >
                      <td className="id-cell">
                        <strong>{item.id}</strong>
                      </td>
                      <td className="text-cell" title={item.title}>
                        {item.title}
                      </td>
                      <td className="text-cell" title={item.si}>
                        {item.si}
                      </td>
                      <td className="text-cell" title={item.pms}>
                        {item.pms}
                      </td>
                      <td className="text-cell" title={item.city}>
                        {item.city}
                      </td>
                      <td className="date-cell">{item.date}</td>
                      <td className="value-cell">
                        {formatCurrency(item.totalValue)}
                      </td>
                      <td className="paid-value-cell">
                        <strong>{formatCurrency(item.paidValue)}</strong>
                      </td>
                      <td className="action-cell">
                        <button
                          onClick={() => handleViewPDF(item.key)}
                          className="pdf-btn"
                          title="Ver PDF"
                        >
                          <i className="fas fa-file-pdf"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td
                      colSpan={6}
                      style={{ textAlign: "center", fontWeight: "bold" }}
                    >
                      Total
                    </td>
                    <td style={{ fontWeight: "bold", textAlign: "right" }}>
                      {formatCurrency(reportSummary.totalValue)}
                    </td>
                    <td style={{ fontWeight: "bold", textAlign: "right" }}>
                      {formatCurrency(reportSummary.totalPaidValue)}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {filteredAndSortedData.length === 0 &&
              paidInvoiceData.length > 0 && (
                <div className="empty-results">
                  <i className="fas fa-search"></i>
                  <p>Nenhum boletim encontrado com os filtros aplicados.</p>
                </div>
              )}
          </div>
        )}

        {paidInvoiceData.length === 0 && !loading && (
          <div className="no-data">
            <i className="fas fa-money-bill-wave"></i>
            <h3>Nenhum dado carregado</h3>
            <p>
              Configure os filtros e clique em "Gerar Relatório" para visualizar
              os dados de pagamento.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaidReport;
